import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Select, TextInput, Label } from "flowbite-react";
import { AlertMessage } from "../components/atoms/AlertMessage";
import { StatusBadge } from "../components/atoms/StatusBadge";
import { Surface } from "../components/atoms/Surface";
import { Spinner } from "../components/atoms/Spinner";
import moment from "moment";
import "moment/locale/fr";
import { momentLocaleOf } from "../utils/momentLocale";
import {
  HiSearch,
  HiX,
  HiLink,
  HiSortAscending,
  HiSortDescending,
  HiUsers,
  HiCreditCard,
  HiPlus,
  HiOutlineTrash,
  HiInformationCircle,
} from "react-icons/hi";
import useAuth from "../hooks/useAuth";
import { PlanTier, usePermissions } from "../utils/permissions";
import { Cap, Level } from "../utils/rbacCatalog";
import { PlansModal } from "../components/modals/PlansModal";
import FeatureUpgradePrompt from "../components/atoms/FeatureUpgradePrompt";
import PaymentModuleService, {
  PaymentSearchResponseDTO,
  PaymentSearchResultDTO,
  PaymentSearchFiltersDTO,
  UserSearchPaginationDTO,
} from "../services/paymentModule";
import { formatPayModDate } from "../services/planChange";
import {
  defaultPaymentDateRange,
  paymentDateBounds,
  payModDay,
} from "./advancedSearchDates";
import { Copy } from "../components/atoms/Copy";
import { toast } from "react-toastify";
import { useTranslation, Trans } from "react-i18next";
import UsersListComponent from "../components/pages/dashboard/users/UsersListComponent";
import { USER_LIST_COMPONENT_TYPE } from "../constants/types";
import Pagination from "../components/public/Pagination";
import { useGetRoleList, useGetTypeList } from "../hooks/users";
import {
  useExportSearchUsersData,
  useExportSearchPaymentsData,
} from "../hooks/entity";
import { CustomButton } from "../components/atoms/CustomButton";
import { DataTable } from "../components/atoms/DataTable";
import { EmptyState } from "../components/atoms/EmptyState";

const FilterPill = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <CustomButton
    size="xs"
    variant={selected ? "primary" : "secondary"}
    className="rounded-full"
    onClick={onClick}
    title={label}
  />
);

const formatAmount = (cents: number, currency: string): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(cents / 100);
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  const parsed = moment(dateStr);
  return parsed.isValid() ? parsed.format("ll") : dateStr;
};

const getStatusColor = (
  status: string
): "success" | "failure" | "warning" | "info" | "gray" | "purple" => {
  const map: Record<
    string,
    "success" | "failure" | "warning" | "info" | "gray" | "purple"
  > = {
    ACTIVE: "success",
    SUCCEEDED: "success",
    CANCELED: "failure",
    ENDED: "gray",
    UNPAID: "warning",
    REFUNDED: "purple",
    ERROR: "failure",
    PROCESSING: "info",
  };
  return map[status] || "gray";
};

const getStatusLabel = (status: string, t: (key: string) => string): string => {
  const map: Record<string, string> = {
    ACTIVE: t("paymentHistory.status.active"),
    SUCCEEDED: t("paymentHistory.status.succeeded"),
    CANCELED: t("paymentHistory.status.canceled"),
    ENDED: t("paymentHistory.status.ended"),
    UNPAID: t("paymentHistory.status.unpaid"),
    REFUNDED: t("paymentHistory.status.refunded"),
    ERROR: t("paymentHistory.status.error"),
    PROCESSING: t("paymentHistory.status.processing"),
  };
  return (map[status] || status).toUpperCase();
};

const getRecurrencyLabel = (
  value: string,
  t: (key: string) => string
): string => {
  const map: Record<string, string> = {
    DAILY: t("paymentModule.recurrencyBadge.daily"),
    WEEKLY: t("paymentModule.recurrencyBadge.weekly"),
    MONTHLY: t("paymentModule.recurrencyBadge.monthly"),
    ANNUALLY: t("paymentModule.recurrencyBadge.annually"),
    YEARLY: t("paymentModule.recurrencyBadge.annually"),
    UNIQUE: t("paymentModule.recurrencyBadge.unique"),
  };
  return (map[value] || value).toUpperCase();
};

const getPaymentTypeLabel = (
  value: string,
  t: (key: string) => string
): string => {
  const map: Record<string, string> = {
    SUBSCRIPTION: t("paymentHistory.type.subscription"),
    UNIQUE: t("paymentHistory.type.unique"),
    FREE: t("paymentHistory.type.free"),
  };
  return (map[value] || value).toUpperCase();
};

const getSortFieldLabel = (
  field: string,
  t: (key: string) => string
): string => {
  const map: Record<string, string> = {
    payment_date: t("dashboard.advancedSearch.sortFields.paymentDate"),
    amount: t("dashboard.advancedSearch.sortFields.amount"),
    status: t("dashboard.advancedSearch.sortFields.status"),
    creation_date: t("dashboard.advancedSearch.sortFields.creationDate"),
    next_payment_date: t("dashboard.advancedSearch.sortFields.nextPaymentDate"),
    user_identification: t(
      "dashboard.advancedSearch.sortFields.userIdentification"
    ),
    plan_code: t("dashboard.advancedSearch.sortFields.planCode"),
  };
  return (map[field] || field.replace(/_/g, " ")).toUpperCase();
};

const sortOrderLabel = (
  order: "ASC" | "DESC",
  t: (key: string) => string
): string =>
  order === "ASC"
    ? t("dashboard.advancedSearch.ascending")
    : t("dashboard.advancedSearch.descending");

const getPaymentStepLabel = (
  step: string,
  t: (key: string) => string
): string => {
  const map: Record<string, string> = {
    INITIAL: t("paymentHistory.step.initial"),
    FINAL: t("paymentHistory.step.final"),
    RENEWED: t("paymentHistory.step.renewed"),
  };
  return (map[step] || step).toUpperCase();
};

type SearchMode = "users" | "payments" | "both";

const AdvancedSearch: React.FC = () => {
  const [t, i18n] = useTranslation();

  useEffect(() => {
    moment.locale(momentLocaleOf(i18n.language));
  }, [i18n.language]);

  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);
  const planResolving =
    user.borrows_owner_plan === true && !user.owner_top_plan;
  const hasAccess = permissions.hasFeatureAccess("ADVANCED_SEARCH");
  const showUpgrade = !hasAccess && !planResolving;
  const [showPlansModal, setShowPlansModal] = useState(false);

  const [filtersData, setFiltersData] =
    useState<PaymentSearchFiltersDTO | null>(null);
  const [filtersLoading, setFiltersLoading] = useState(hasAccess);
  const [filtersError, setFiltersError] = useState<string | null>(null);

  const [lastSearchMode, setLastSearchMode] = useState<SearchMode | null>(null);

  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [paymentResults, setPaymentResults] =
    useState<PaymentSearchResponseDTO | null>(null);

  const [userResults, setUserResults] = useState<any[] | null>(null);
  const [userPagination, setUserPagination] =
    useState<UserSearchPaginationDTO | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());

  const MAX_QUERY_TERMS = 3;
  const [queryTerms, setQueryTerms] = useState<string[]>([""]);

  const updateQueryTerm = (index: number, value: string) => {
    setQueryTerms((prev) => prev.map((t, i) => (i === index ? value : t)));
  };
  const addQueryTerm = () => {
    setQueryTerms((prev) =>
      prev.length < MAX_QUERY_TERMS ? [...prev, ""] : prev
    );
  };
  const removeQueryTerm = (index: number) => {
    setQueryTerms((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );
  };
  const activeTerms = queryTerms.map((t) => t.trim()).filter(Boolean);

  const [userRole, setUserRole] = useState("");
  const [userType, setUserType] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [lastConnFrom, setLastConnFrom] = useState("");
  const [lastConnTo, setLastConnTo] = useState("");
  const [userSortBy, setUserSortBy] = useState<
    "CREATION_DATE" | "LAST_CONNECTION_DATE"
  >("CREATION_DATE");
  const [userSortOrder, setUserSortOrder] = useState<"ASC" | "DESC">("DESC");

  const { data: roleListData } = useGetRoleList({
    user_identification: customer_identification,
    entity_identification: user.entity_identification,
    enabled: permissions.can(Cap.USERS_USER_ROLE, Level.VIEW),
  });
  const { data: typeListData } = useGetTypeList({
    user_identification: customer_identification,
    entity_identification: user.entity_identification,
    enabled: permissions.can(Cap.USERS_USER_TYPE, Level.VIEW),
  });
  const roleOptions: string[] = (roleListData || [])
    .map((r: any) => r?.user_role)
    .filter(Boolean);
  const typeOptions: string[] = (typeListData || [])
    .map((tp: any) => tp?.user_type)
    .filter(Boolean);

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPlanCodes, setSelectedPlanCodes] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<string[]>(
    []
  );
  const [selectedRecurrencies, setSelectedRecurrencies] = useState<string[]>(
    []
  );
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");

  const [page, setPage] = useState(0);
  const [paymentPage, setPaymentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("payment_date");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  const { mutate: mutateUsers, isLoading: isUsersExportLoading } =
    useExportSearchUsersData();
  const { mutate: mutatePayments, isLoading: isPaymentsExportLoading } =
    useExportSearchPaymentsData();

  useEffect(() => {
    if (hasAccess) loadFilters();
  }, [hasAccess]);

  const loadFilters = async () => {
    setFiltersLoading(true);
    setFiltersError(null);
    try {
      const response = await PaymentModuleService.getPaymentSearchFilters(
        customer_identification,
        user.entity_identification
      );
      if (response.data?.element) {
        const filters = response.data.element;
        setFiltersData(filters);
        const range = defaultPaymentDateRange(filters.payment_date_range);
        setDateFrom(range.from);
        setDateTo(range.to);
      } else {
        setFiltersError(
          response.data?.message ||
            t("dashboard.advancedSearch.failedToLoadFilters")
        );
      }
    } catch (err: any) {
      setFiltersError(
        err?.response?.data?.message ||
          t("dashboard.advancedSearch.failedToLoadFiltersGeneric")
      );
    } finally {
      setFiltersLoading(false);
    }
  };

  const hasRequiredPaymentFilters =
    selectedPaymentTypes.length > 0 ||
    selectedRecurrencies.length > 0 ||
    selectedPlanCodes.length > 0 ||
    amountMin !== "" ||
    amountMax !== "" ||
    selectedStatuses.length > 0;

  const hasUserText = activeTerms.length > 0;
  const hasUserFilters =
    userRole !== "" ||
    userType !== "" ||
    createdFrom !== "" ||
    createdTo !== "" ||
    lastConnFrom !== "" ||
    lastConnTo !== "";
  const hasUserQuery = hasUserText || hasUserFilters;

  const detectSearchMode = (): SearchMode | null => {
    if (hasUserQuery && hasRequiredPaymentFilters) return "both";
    if (hasUserQuery) return "users";
    if (hasRequiredPaymentFilters) return "payments";
    return null;
  };

  const buildPaymentFilters = (paymentPageArg?: number) => {
    const filters: any = {
      page: paymentPageArg ?? paymentPage,
      size: pageSize,
      sort_by: sortBy,
      sort_direction: sortDirection,
      ...paymentDateBounds(dateFrom, dateTo),
    };
    if (selectedStatuses.length > 0) filters.statuses = selectedStatuses;
    if (selectedPlanCodes.length > 0) filters.plan_codes = selectedPlanCodes;
    if (selectedPaymentTypes.length > 0)
      filters.payment_types = selectedPaymentTypes;
    if (selectedRecurrencies.length > 0)
      filters.recurrencies = selectedRecurrencies;
    if (amountMin) filters.amount_min = Math.round(parseFloat(amountMin) * 100);
    if (amountMax) filters.amount_max = Math.round(parseFloat(amountMax) * 100);
    return filters;
  };

  const executeSearch = async (
    opts: {
      userPageArg?: number;
      paymentPageArg?: number;
      scope?: "both" | "users" | "payments";
    } = {}
  ) => {
    const { userPageArg, paymentPageArg, scope = "both" } = opts;
    const mode = detectSearchMode();

    if (!mode) {
      toast.error(t("dashboard.advancedSearch.errorNoFilters"));
      return;
    }

    if (hasUserText && activeTerms.some((term) => term.length < 5)) {
      toast.error(t("dashboard.advancedSearch.errorMinLength"));
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setHasSearched(true);
    setLastSearchMode(mode);

    try {
      const currentUserPage = userPageArg ?? page;
      const currentPaymentPage = paymentPageArg ?? paymentPage;
      const paymentFilters = buildPaymentFilters(currentPaymentPage);

      const body: any = {
        ...paymentFilters,
        limit: pageSize,
        offset: currentUserPage * pageSize,
      };

      if (hasUserText) {
        body.query = activeTerms;
      }
      if (userRole) body.user_role = userRole;
      if (userType) body.user_type = userType;
      if (createdFrom) body.created_from = createdFrom;
      if (createdTo) body.created_to = createdTo;
      if (lastConnFrom) body.last_connection_from = lastConnFrom;
      if (lastConnTo) body.last_connection_to = lastConnTo;
      if (hasUserQuery) {
        body.user_sort_by = userSortBy;
        body.user_sort_order = userSortOrder;
      }

      const response = await PaymentModuleService.combinedSearch(
        customer_identification,
        user.entity_identification,
        body
      );

      const data = response.data?.element || response.data;

      if (scope === "both" || scope === "users") {
        if (data?.users) {
          setUserResults(data.users);
          setUserPagination(data.user_pagination);
        } else {
          setUserResults(null);
          setUserPagination(null);
        }
      }

      if (scope === "both" || scope === "payments") {
        if (data?.payments) {
          setPaymentResults(data.payments);
        } else {
          setPaymentResults(null);
        }
      }
    } catch (err: any) {
      setSearchError(
        err?.response?.data?.message ||
          err?.message ||
          t("dashboard.advancedSearch.errorSearchFailed")
      );
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setPaymentPage(0);
    executeSearch({ userPageArg: 0, paymentPageArg: 0, scope: "both" });
  };

  const handleUserPageChange = (newPage: number) => {
    setPage(newPage);
    executeSearch({ userPageArg: newPage, scope: "users" });
  };

  const handlePaymentPageChange = (newPage: number) => {
    setPaymentPage(newPage);
    executeSearch({ paymentPageArg: newPage, scope: "payments" });
  };

  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(field);
      setSortDirection("DESC");
    }
  };

  useEffect(() => {
    if (
      hasSearched &&
      (lastSearchMode === "payments" || lastSearchMode === "both")
    ) {
      setPaymentPage(0);
      executeSearch({ paymentPageArg: 0, scope: "payments" });
    }
  }, [sortBy, sortDirection]);

  useEffect(() => {
    if (
      hasSearched &&
      (lastSearchMode === "users" || lastSearchMode === "both")
    ) {
      setPage(0);
      executeSearch({ userPageArg: 0, scope: "users" });
    }
  }, [userSortBy, userSortOrder]);

  const handleClearFilters = () => {
    setQueryTerms([""]);
    setUserRole("");
    setUserType("");
    setCreatedFrom("");
    setCreatedTo("");
    setLastConnFrom("");
    setLastConnTo("");
    setUserSortBy("CREATION_DATE");
    setUserSortOrder("DESC");
    setSelectedStatuses([]);
    setSelectedPlanCodes([]);
    const range = defaultPaymentDateRange(filtersData?.payment_date_range);
    setDateFrom(range.from);
    setDateTo(range.to);
    setSelectedPaymentTypes([]);
    setSelectedRecurrencies([]);
    setAmountMin("");
    setAmountMax("");
    setPage(0);
    setPaymentPage(0);
    setPaymentResults(null);
    setUserResults(null);
    setUserPagination(null);
    setSelectedRowIds(new Set());
    setHasSearched(false);
    setLastSearchMode(null);
  };

  const toggleArrayValue = (
    arr: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string
  ) => {
    setter(
      arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
    );
  };

  const detectedMode = detectSearchMode();

  const triggerCsvDownload = (data: any, filename: string) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportSearchUsersData = () => {
    const searchBody: any = {};
    if (hasUserText) searchBody.query = activeTerms;
    if (userRole) searchBody.user_role = userRole;
    if (userType) searchBody.user_type = userType;
    if (createdFrom) searchBody.created_from = createdFrom;
    if (createdTo) searchBody.created_to = createdTo;
    if (lastConnFrom) searchBody.last_connection_from = lastConnFrom;
    if (lastConnTo) searchBody.last_connection_to = lastConnTo;
    if (hasUserQuery) {
      searchBody.user_sort_by = userSortBy;
      searchBody.user_sort_order = userSortOrder;
    }

    mutateUsers(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        searchBody,
      },
      {
        onSuccess: (data) => triggerCsvDownload(data?.data, "user_results.csv"),
      }
    );
  };

  const handleExportPaymentData = () => {
    const searchBody: any = paymentDateBounds(dateFrom, dateTo);
    if (selectedStatuses.length > 0) searchBody.statuses = selectedStatuses;
    if (selectedPaymentTypes.length > 0)
      searchBody.payment_types = selectedPaymentTypes;
    if (selectedRecurrencies.length > 0)
      searchBody.recurrencies = selectedRecurrencies;
    if (selectedPlanCodes.length > 0) searchBody.plan_codes = selectedPlanCodes;
    if (amountMin)
      searchBody.amount_min = Math.round(parseFloat(amountMin) * 100);
    if (amountMax)
      searchBody.amount_max = Math.round(parseFloat(amountMax) * 100);
    searchBody.sort_by = sortBy;
    searchBody.sort_direction = sortDirection;

    mutatePayments(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        searchBody,
      },
      {
        onSuccess: (data) =>
          triggerCsvDownload(data?.data, "payment_results.csv"),
      }
    );
  };

  const earliestPaymentDay = payModDay(
    filtersData?.payment_date_range?.earliest_payment
  );
  const latestPaymentDay = payModDay(
    filtersData?.payment_date_range?.latest_payment
  );

  if (filtersLoading) {
    return (
      <div className="advanced-search-page">
        <Helmet>
          <title>
            {t("dashboard.advancedSearch.title")} | Super User Manager
          </title>
        </Helmet>
        <div className="container mx-auto py-6 px-4">
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
            <span className="ml-3 text-gray-500">
              {t("dashboard.advancedSearch.loadingFilters")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="advanced-search-page">
      <Helmet>
        <title>
          {t("dashboard.advancedSearch.title")} | Super User Manager
        </title>
        <meta
          name="description"
          content={t("dashboard.advancedSearch.title") as string}
        />
      </Helmet>

      <div className="container mx-auto py-6 px-4 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("dashboard.advancedSearch.title")}
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("dashboard.advancedSearch.subtitle")}
          </p>
        </div>

        {filtersError && hasAccess && (
          <AlertMessage color="warning" className="mb-4">
            <span>
              {t("dashboard.advancedSearch.filtersErrorAlert", {
                error: filtersError,
              })}
            </span>
          </AlertMessage>
        )}

        <div className="relative">
          {showUpgrade && (
            <div className="absolute inset-0 z-10 flex items-start justify-center pt-6 px-4">
              <div className="w-full max-w-2xl">
                <FeatureUpgradePrompt
                  title={t("dashboard.advancedSearch.upgrade.title") as string}
                  description={
                    t("dashboard.advancedSearch.upgrade.description") as string
                  }
                  benefits={[
                    t("dashboard.advancedSearch.upgrade.benefit1") as string,
                    t("dashboard.advancedSearch.upgrade.benefit2") as string,
                    t("dashboard.advancedSearch.upgrade.benefit3") as string,
                    t("dashboard.advancedSearch.upgrade.benefit4") as string,
                  ]}
                  onUpgrade={() => setShowPlansModal(true)}
                />
              </div>
            </div>
          )}

          <div
            className={
              showUpgrade
                ? "pointer-events-none select-none opacity-40 blur-[2px]"
                : ""
            }
            aria-hidden={showUpgrade}
          >
            <Surface className="mb-4">
              <div className="space-y-4">
                <div className="flex items-start gap-2">
                  <HiUsers
                    className="text-green-600 mt-0.5 shrink-0"
                    size={18}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-gray-700">
                      {t("dashboard.advancedSearch.userSearch")}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {queryTerms.map((term, idx) => (
                    <React.Fragment key={idx}>
                      {idx > 0 && (
                        <div className="flex items-center gap-2 py-0.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wide">
                            {t("dashboard.advancedSearch.andOperator")}
                          </span>
                          <span className="text-xs text-gray-400">
                            {t("dashboard.advancedSearch.andOperatorHint")}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <TextInput
                          sizing="sm"
                          type="text"
                          className="flex-1"
                          placeholder={
                            (idx === 0
                              ? t(
                                  "dashboard.advancedSearch.userSearchPlaceholder"
                                )
                              : t(
                                  "dashboard.advancedSearch.userSearchPlaceholderMore"
                                )) as string
                          }
                          value={term}
                          onChange={(e) => updateQueryTerm(idx, e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          icon={HiSearch}
                        />
                        {queryTerms.length > 1 && (
                          <CustomButton
                            variant="secondary"
                            size="icon"
                            onClick={() => removeQueryTerm(idx)}
                            aria-label={
                              t("dashboard.advancedSearch.removeTerm") as string
                            }
                            className="shrink-0 text-gray-400 enabled:hover:text-red-600"
                            tooltip={
                              t("dashboard.advancedSearch.removeTerm") as string
                            }
                            icon={<HiOutlineTrash size={18} />}
                          />
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                  {queryTerms.length < MAX_QUERY_TERMS && (
                    <button
                      type="button"
                      onClick={addQueryTerm}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      <HiPlus size={14} />
                      {t("dashboard.advancedSearch.addTerm")}
                    </button>
                  )}
                  {queryTerms.length > 1 && (
                    <p className="flex items-start gap-1.5 text-xs text-amber-600">
                      <HiInformationCircle
                        className="mt-0.5 shrink-0"
                        size={14}
                      />
                      <span>
                        <Trans
                          i18nKey="dashboard.advancedSearch.multiTermHint"
                          components={{
                            strong: <strong className="font-semibold" />,
                          }}
                        />
                      </span>
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label
                      htmlFor="user-role-filter"
                      value={t("dashboard.advancedSearch.userRole") as string}
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <Select
                      id="user-role-filter"
                      sizing="sm"
                      value={userRole}
                      onChange={(e) => setUserRole(e.target.value)}
                    >
                      <option value="">
                        {t("dashboard.advancedSearch.allRoles")}
                      </option>
                      {roleOptions.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Label
                      htmlFor="user-type-filter"
                      value={t("dashboard.advancedSearch.userType") as string}
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <Select
                      id="user-type-filter"
                      sizing="sm"
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                    >
                      <option value="">
                        {t("dashboard.advancedSearch.allTypes")}
                      </option>
                      {typeOptions.map((tp) => (
                        <option key={tp} value={tp}>
                          {tp}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="lg:col-span-2">
                    <Label
                      value={t("dashboard.advancedSearch.sortBy") as string}
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <div className="flex items-center gap-2">
                      <Select
                        sizing="sm"
                        value={userSortBy}
                        onChange={(e) =>
                          setUserSortBy(
                            e.target.value as
                              | "CREATION_DATE"
                              | "LAST_CONNECTION_DATE"
                          )
                        }
                        className="flex-1 min-w-0"
                      >
                        <option value="CREATION_DATE">
                          {t("dashboard.advancedSearch.userSort.creationDate")}
                        </option>
                        <option value="LAST_CONNECTION_DATE">
                          {t(
                            "dashboard.advancedSearch.userSort.lastConnection"
                          )}
                        </option>
                      </Select>
                      <CustomButton
                        variant="secondary"
                        size="icon"
                        onClick={() =>
                          setUserSortOrder((d) =>
                            d === "ASC" ? "DESC" : "ASC"
                          )
                        }
                        className="shrink-0 text-gray-500 enabled:hover:text-blue-600"
                        aria-label={sortOrderLabel(userSortOrder, t)}
                        tooltip={sortOrderLabel(userSortOrder, t)}
                        icon={
                          <span className="flex">
                            {userSortOrder === "ASC" ? (
                              <HiSortAscending size={18} />
                            ) : (
                              <HiSortDescending size={18} />
                            )}
                          </span>
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <fieldset className="border border-gray-200 rounded-lg p-3">
                    <legend className="px-1 text-xs font-semibold text-gray-700">
                      {t("dashboard.advancedSearch.registrationDate")}
                    </legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label
                          htmlFor="user-created-from"
                          value={
                            t("dashboard.advancedSearch.rangeFrom") as string
                          }
                          className="mb-1 block text-xs font-medium text-gray-600"
                        />
                        <TextInput
                          id="user-created-from"
                          type="date"
                          sizing="sm"
                          value={createdFrom}
                          max={createdTo || undefined}
                          onChange={(e) => setCreatedFrom(e.target.value)}
                        />
                        <span className="mt-1 block text-xs text-gray-500">
                          {t("dashboard.advancedSearch.dateFormatHint")}
                        </span>
                      </div>
                      <div>
                        <Label
                          htmlFor="user-created-to"
                          value={
                            t("dashboard.advancedSearch.rangeTo") as string
                          }
                          className="mb-1 block text-xs font-medium text-gray-600"
                        />
                        <TextInput
                          id="user-created-to"
                          type="date"
                          sizing="sm"
                          value={createdTo}
                          min={createdFrom || undefined}
                          onChange={(e) => setCreatedTo(e.target.value)}
                        />
                        <span className="mt-1 block text-xs text-gray-500">
                          {t("dashboard.advancedSearch.dateFormatHint")}
                        </span>
                      </div>
                    </div>
                  </fieldset>

                  <fieldset className="border border-gray-200 rounded-lg p-3">
                    <legend className="px-1 text-xs font-semibold text-gray-700">
                      {t("dashboard.advancedSearch.lastConnectionDate")}
                    </legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label
                          htmlFor="user-lastconn-from"
                          value={
                            t("dashboard.advancedSearch.rangeFrom") as string
                          }
                          className="mb-1 block text-xs font-medium text-gray-600"
                        />
                        <TextInput
                          id="user-lastconn-from"
                          type="date"
                          sizing="sm"
                          value={lastConnFrom}
                          max={lastConnTo || undefined}
                          onChange={(e) => setLastConnFrom(e.target.value)}
                        />
                        <span className="mt-1 block text-xs text-gray-500">
                          {t("dashboard.advancedSearch.dateFormatHint")}
                        </span>
                      </div>
                      <div>
                        <Label
                          htmlFor="user-lastconn-to"
                          value={
                            t("dashboard.advancedSearch.rangeTo") as string
                          }
                          className="mb-1 block text-xs font-medium text-gray-600"
                        />
                        <TextInput
                          id="user-lastconn-to"
                          type="date"
                          sizing="sm"
                          value={lastConnTo}
                          min={lastConnFrom || undefined}
                          onChange={(e) => setLastConnTo(e.target.value)}
                        />
                        <span className="mt-1 block text-xs text-gray-500">
                          {t("dashboard.advancedSearch.dateFormatHint")}
                        </span>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </div>
            </Surface>

            <Surface className="mb-6">
              <div className="space-y-4">
                <div className="flex items-start gap-2">
                  <HiCreditCard
                    className="text-blue-600 mt-0.5 shrink-0"
                    size={18}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-gray-700">
                      {t("dashboard.advancedSearch.paymentFilters")}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <fieldset className="border border-gray-200 rounded-lg p-3 md:col-span-2">
                    <legend className="px-1 text-xs font-semibold text-gray-700">
                      {t("dashboard.advancedSearch.paymentDate")}
                    </legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label
                          htmlFor="date-from"
                          value={
                            t("dashboard.advancedSearch.rangeFrom") as string
                          }
                          className="mb-1 block text-xs font-medium text-gray-600"
                        />
                        <TextInput
                          id="date-from"
                          type="date"
                          sizing="sm"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          min={earliestPaymentDay}
                          max={dateTo || latestPaymentDay}
                        />
                        <span className="mt-1 block text-xs text-gray-500">
                          {t("dashboard.advancedSearch.dateFormatHint")}
                        </span>
                      </div>
                      <div>
                        <Label
                          htmlFor="date-to"
                          value={
                            t("dashboard.advancedSearch.rangeTo") as string
                          }
                          className="mb-1 block text-xs font-medium text-gray-600"
                        />
                        <TextInput
                          id="date-to"
                          type="date"
                          sizing="sm"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          min={dateFrom || earliestPaymentDay}
                          max={latestPaymentDay}
                        />
                        <span className="mt-1 block text-xs text-gray-500">
                          {t("dashboard.advancedSearch.dateFormatHint")}
                        </span>
                      </div>
                    </div>
                  </fieldset>
                  <div>
                    <Label
                      value={
                        t("dashboard.advancedSearch.paymentType") as string
                      }
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <div className="flex flex-wrap gap-1.5 min-h-[38px] p-1.5 border border-gray-300 rounded-lg bg-gray-50">
                      {filtersData?.available_payment_types?.map((pt) => (
                        <FilterPill
                          key={pt}
                          label={getPaymentTypeLabel(pt, t)}
                          selected={selectedPaymentTypes.includes(pt)}
                          onClick={() =>
                            toggleArrayValue(
                              selectedPaymentTypes,
                              setSelectedPaymentTypes,
                              pt
                            )
                          }
                        />
                      )) || (
                        <span className="text-gray-400 text-xs px-1 self-center">
                          —
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label
                      value={t("dashboard.advancedSearch.recurrency") as string}
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <div className="flex flex-wrap gap-1.5 min-h-[38px] p-1.5 border border-gray-300 rounded-lg bg-gray-50">
                      {filtersData?.available_recurrencies?.map((r) => (
                        <FilterPill
                          key={r}
                          label={getRecurrencyLabel(r, t)}
                          selected={selectedRecurrencies.includes(r)}
                          onClick={() =>
                            toggleArrayValue(
                              selectedRecurrencies,
                              setSelectedRecurrencies,
                              r
                            )
                          }
                        />
                      )) || (
                        <span className="text-gray-400 text-xs px-1 self-center">
                          —
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label
                      htmlFor="plan-filter"
                      value={t("dashboard.advancedSearch.plan") as string}
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <Select
                      id="plan-filter"
                      sizing="sm"
                      value={
                        selectedPlanCodes.length === 1
                          ? selectedPlanCodes[0]
                          : ""
                      }
                      onChange={(e) =>
                        setSelectedPlanCodes(
                          e.target.value ? [e.target.value] : []
                        )
                      }
                    >
                      <option value="">
                        {t("dashboard.advancedSearch.allPlans")}
                      </option>
                      {filtersData?.available_plan_codes?.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Label
                      value={
                        t("dashboard.advancedSearch.amountRange") as string
                      }
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <div className="flex items-center gap-2">
                      <TextInput
                        sizing="sm"
                        type="number"
                        step="0.01"
                        min="1"
                        max="10000"
                        placeholder={
                          t("dashboard.advancedSearch.amountMinPlaceholder", {
                            value: filtersData?.amount_range
                              ? (
                                  filtersData.amount_range.min_amount / 100
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                })
                              : "1.00",
                          }) as string
                        }
                        value={amountMin}
                        onChange={(e) => setAmountMin(e.target.value)}
                        className="flex-1"
                      />
                      <span className="text-gray-400 text-xs">
                        {t("dashboard.advancedSearch.amountTo")}
                      </span>
                      <TextInput
                        sizing="sm"
                        type="number"
                        step="0.01"
                        min="1"
                        max="10000"
                        placeholder={
                          t("dashboard.advancedSearch.amountMaxPlaceholder", {
                            value: filtersData?.amount_range
                              ? (
                                  filtersData.amount_range.max_amount / 100
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                })
                              : "10,000.00",
                          }) as string
                        }
                        value={amountMax}
                        onChange={(e) => setAmountMax(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label
                      htmlFor="status-filter"
                      value={t("dashboard.advancedSearch.status") as string}
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <div className="flex flex-wrap gap-1.5 min-h-[38px] p-1.5 border border-gray-300 rounded-lg bg-gray-50">
                      {filtersData?.available_statuses?.map((s) => (
                        <FilterPill
                          key={s}
                          label={getStatusLabel(s, t)}
                          selected={selectedStatuses.includes(s)}
                          onClick={() =>
                            toggleArrayValue(
                              selectedStatuses,
                              setSelectedStatuses,
                              s
                            )
                          }
                        />
                      )) || (
                        <span className="text-gray-400 text-xs px-1 self-center">
                          {t("dashboard.advancedSearch.noStatuses")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label
                      value={t("dashboard.advancedSearch.sortBy") as string}
                      className="mb-1 block text-xs font-medium text-gray-600"
                    />
                    <div className="flex items-center gap-2">
                      <Select
                        sizing="sm"
                        value={sortBy}
                        onChange={(e) => handleSortChange(e.target.value)}
                        className="flex-1 min-w-0"
                      >
                        {filtersData?.available_sort_fields?.map((sf) => (
                          <option key={sf} value={sf}>
                            {getSortFieldLabel(sf, t)}
                          </option>
                        )) || (
                          <>
                            <option value="payment_date">
                              {getSortFieldLabel("payment_date", t)}
                            </option>
                            <option value="amount">
                              {getSortFieldLabel("amount", t)}
                            </option>
                            <option value="status">
                              {getSortFieldLabel("status", t)}
                            </option>
                          </>
                        )}
                      </Select>
                      <CustomButton
                        variant="secondary"
                        size="icon"
                        onClick={() =>
                          setSortDirection((d) =>
                            d === "ASC" ? "DESC" : "ASC"
                          )
                        }
                        className="shrink-0 text-gray-500 enabled:hover:text-blue-600"
                        aria-label={sortOrderLabel(sortDirection, t)}
                        tooltip={sortOrderLabel(sortDirection, t)}
                        icon={
                          <span className="flex">
                            {sortDirection === "ASC" ? (
                              <HiSortAscending size={18} />
                            ) : (
                              <HiSortDescending size={18} />
                            )}
                          </span>
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Surface>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              <CustomButton
                variant="primary"
                onClick={handleSearch}
                disabled={searchLoading}
                className="flex-1 sm:flex-none whitespace-nowrap"
                icon={
                  searchLoading ? (
                    <span translate="no">
                      <Spinner size="sm" />
                    </span>
                  ) : (
                    <HiSearch size={16} />
                  )
                }
                title={t("common.search")}
              />
              <CustomButton
                variant="secondary"
                onClick={handleClearFilters}
                className="flex-1 sm:flex-none whitespace-nowrap"
                icon={<HiX size={14} />}
                title={t("dashboard.advancedSearch.clearAll")}
              />

              {detectedMode && (
                <span className="hidden sm:inline-flex items-center gap-1.5 ml-2 px-2.5 py-1 bg-gray-100 rounded-full text-xs text-gray-500">
                  {detectedMode === "both" ? (
                    <>
                      <HiUsers size={14} />
                      <HiCreditCard size={14} />
                      {t("dashboard.advancedSearch.willSearchBoth")}
                    </>
                  ) : detectedMode === "users" ? (
                    <>
                      <HiUsers size={14} />
                      {t("dashboard.advancedSearch.willSearchUsers")}
                    </>
                  ) : (
                    <>
                      <HiCreditCard size={14} />
                      {t("dashboard.advancedSearch.willSearchPayments")}
                    </>
                  )}
                </span>
              )}
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {t("dashboard.advancedSearch.show")}
                </span>
                <Select
                  sizing="sm"
                  value={pageSize.toString()}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setPage(0);
                  }}
                  className="w-20 shrink-0"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </Select>
              </div>
            </div>

            {searchError && (
              <AlertMessage color="failure" className="mb-4">
                <span>{searchError}</span>
              </AlertMessage>
            )}

            {searchLoading && (
              <div className="flex items-center justify-center py-16">
                <Spinner size="lg" />
                <span className="ml-3 text-gray-500">
                  {t("dashboard.advancedSearch.searching")}
                </span>
              </div>
            )}

            {!searchLoading &&
              (lastSearchMode === "users" || lastSearchMode === "both") &&
              userResults && (
                <>
                  {userResults.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <HiUsers className="text-blue-600" size={16} />
                          <span className="text-sm font-semibold text-gray-700">
                            {t("dashboard.advancedSearch.userResults")}
                          </span>
                        </div>
                        <CustomButton
                          variant="none"
                          size="sm"
                          onClick={handleExportSearchUsersData}
                          disabled={isUsersExportLoading}
                          isLoading={isUsersExportLoading}
                          icon="download"
                          className="text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors disabled:text-gray-300"
                          title={t(
                            "dashboard.develop.exportData.export",
                            "Export Users"
                          )}
                        />
                      </div>
                      <div className="overflow-x-auto">
                        <UsersListComponent
                          users={userResults}
                          isError={false}
                          isLoading={false}
                          isSuccess={true}
                          refetch={() => executeSearch()}
                          selectedRowIds={selectedRowIds}
                          setSelectedRowIds={setSelectedRowIds}
                          type={USER_LIST_COMPONENT_TYPE.USERS}
                          offset={page * pageSize}
                          setOffset={(val: number) => {
                            const newPage = Math.floor(val / pageSize);
                            handleUserPageChange(newPage);
                          }}
                          limit={userResults.length}
                          pagination={
                            userPagination
                              ? {
                                  total: userPagination.total,
                                  total_pages: userPagination.total_pages,
                                }
                              : undefined
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <Surface>
                      <div className="text-center py-10">
                        <HiUsers
                          className="mx-auto mb-3 text-gray-300"
                          size={48}
                        />
                        <p className="text-gray-500 text-sm">
                          {t("dashboard.advancedSearch.noUsersFound")}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                          {t("dashboard.advancedSearch.noUsersFoundHint")}
                        </p>
                      </div>
                    </Surface>
                  )}
                </>
              )}

            {!searchLoading &&
              (lastSearchMode === "payments" || lastSearchMode === "both") &&
              paymentResults && (
                <>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <HiCreditCard className="text-blue-600" size={16} />
                      <span className="text-sm font-semibold text-gray-700">
                        {t("dashboard.advancedSearch.paymentResults")}
                      </span>
                    </div>
                    {paymentResults.results.length > 0 && (
                      <CustomButton
                        variant="none"
                        size="sm"
                        onClick={handleExportPaymentData}
                        disabled={isPaymentsExportLoading}
                        isLoading={isPaymentsExportLoading}
                        icon="download"
                        className="text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors disabled:text-gray-300"
                        title={t(
                          "dashboard.develop.exportData.export",
                          "Export Payments"
                        )}
                      />
                    )}
                  </div>

                  {paymentResults.results.length > 0 ? (
                    <>
                      <div className="md:hidden rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                        {paymentResults.results.map(
                          (result: PaymentSearchResultDTO, idx: number) => (
                            <div
                              key={`${result.payment_identification}-${idx}-m`}
                              className="p-4"
                            >
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 text-xs font-mono">
                                    <Copy
                                      target={result.user_identification}
                                      truncate
                                    />
                                    <a
                                      href={`/control-panel/users/${result.user_identification}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors shrink-0"
                                      title={
                                        t(
                                          "dashboard.advancedSearch.viewUserDetails"
                                        ) as string
                                      }
                                    >
                                      <HiSearch size={14} />
                                    </a>
                                  </div>
                                  <div className="mt-1 text-xs text-gray-600 break-all">
                                    {result.plan_code}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                  <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                                    {formatAmount(
                                      result.plan_amount,
                                      result.plan_currency
                                    )}
                                  </div>
                                  <StatusBadge
                                    color={getStatusColor(
                                      result.current_status
                                    )}
                                    size="xs"
                                  >
                                    {getStatusLabel(result.current_status, t)}
                                  </StatusBadge>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                                <div>
                                  <div className="text-gray-400 uppercase tracking-wide text-xs">
                                    {t("dashboard.advancedSearch.tableDate")}
                                  </div>
                                  <div className="text-sm text-gray-700">
                                    {formatPayModDate(
                                      result.payment_date,
                                      "ll",
                                      "—"
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-400 uppercase tracking-wide text-xs">
                                    {t(
                                      "dashboard.advancedSearch.tableNextPayment"
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-700">
                                    {result.next_payment_date
                                      ? formatDate(result.next_payment_date)
                                      : "—"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-400 uppercase tracking-wide text-xs">
                                    {t("dashboard.advancedSearch.tableStep")}
                                  </div>
                                  <div>
                                    <StatusBadge color="gray" size="xs">
                                      {getPaymentStepLabel(
                                        result.payment_step,
                                        t
                                      )}
                                    </StatusBadge>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-400 uppercase tracking-wide text-xs">
                                    {t(
                                      "dashboard.advancedSearch.tablePaymentLink"
                                    )}
                                  </div>
                                  <div>
                                    {result.payment_request_unique_link ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium break-all">
                                        <HiLink size={12} />
                                        {result.payment_request_unique_link}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>

                      <DataTable wrapperClassName="hidden md:block">
                        <DataTable.Head>
                          <DataTable.HeadCell>
                            {t("dashboard.advancedSearch.tableUser")}
                          </DataTable.HeadCell>
                          <DataTable.HeadCell>
                            {t("dashboard.advancedSearch.tablePlan")}
                          </DataTable.HeadCell>
                          <DataTable.HeadCell>
                            {t("dashboard.advancedSearch.tableStatus")}
                          </DataTable.HeadCell>
                          <DataTable.HeadCell numeric>
                            {t("dashboard.advancedSearch.tableAmount")}
                          </DataTable.HeadCell>
                          <DataTable.HeadCell>
                            {t("dashboard.advancedSearch.tableDate")}
                          </DataTable.HeadCell>
                          <DataTable.HeadCell>
                            {t("dashboard.advancedSearch.tableStep")}
                          </DataTable.HeadCell>
                          <DataTable.HeadCell>
                            {t("dashboard.advancedSearch.tableNextPayment")}
                          </DataTable.HeadCell>
                          <DataTable.HeadCell>
                            {t("dashboard.advancedSearch.tablePaymentLink")}
                          </DataTable.HeadCell>
                        </DataTable.Head>
                        <DataTable.Body>
                          {paymentResults.results.map(
                            (result: PaymentSearchResultDTO, idx: number) => (
                              <DataTable.Row
                                key={`${result.payment_identification}-${idx}`}
                              >
                                <DataTable.Cell className="whitespace-nowrap font-mono text-xs text-gray-900">
                                  <div className="flex items-center gap-1.5">
                                    <Copy
                                      target={result.user_identification}
                                      truncate
                                    />
                                    <a
                                      href={`/control-panel/users/${result.user_identification}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                      title={
                                        t(
                                          "dashboard.advancedSearch.viewUserDetails"
                                        ) as string
                                      }
                                    >
                                      <HiSearch size={14} />
                                    </a>
                                  </div>
                                </DataTable.Cell>
                                <DataTable.Cell>
                                  {result.plan_code}
                                </DataTable.Cell>
                                <DataTable.Cell>
                                  <StatusBadge
                                    color={getStatusColor(
                                      result.current_status
                                    )}
                                    size="xs"
                                  >
                                    {getStatusLabel(result.current_status, t)}
                                  </StatusBadge>
                                </DataTable.Cell>
                                <DataTable.Cell
                                  numeric
                                  className="whitespace-nowrap font-medium text-gray-900"
                                >
                                  {formatAmount(
                                    result.plan_amount,
                                    result.plan_currency
                                  )}
                                </DataTable.Cell>
                                <DataTable.Cell className="whitespace-nowrap tabular-nums">
                                  {formatPayModDate(
                                    result.payment_date,
                                    "ll",
                                    "—"
                                  )}
                                </DataTable.Cell>
                                <DataTable.Cell>
                                  <StatusBadge color="gray" size="xs">
                                    {getPaymentStepLabel(
                                      result.payment_step,
                                      t
                                    )}
                                  </StatusBadge>
                                </DataTable.Cell>
                                <DataTable.Cell className="whitespace-nowrap tabular-nums text-gray-500">
                                  {result.next_payment_date
                                    ? formatDate(result.next_payment_date)
                                    : "—"}
                                </DataTable.Cell>
                                <DataTable.Cell>
                                  {result.payment_request_unique_link ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                                      <HiLink size={12} />
                                      {result.payment_request_unique_link}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </DataTable.Cell>
                              </DataTable.Row>
                            )
                          )}
                        </DataTable.Body>
                      </DataTable>
                    </>
                  ) : (
                    <Surface>
                      <div className="text-center py-10">
                        <HiSearch
                          className="mx-auto mb-3 text-gray-300"
                          size={48}
                        />
                        <p className="text-gray-500 text-sm">
                          {t("dashboard.advancedSearch.noPaymentsFound")}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                          {t("dashboard.advancedSearch.noPaymentsFoundHint")}
                        </p>
                      </div>
                    </Surface>
                  )}

                  {paymentResults.total_pages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 my-6 px-2">
                      <div className="text-sm text-gray-600 text-center sm:text-left">
                        <span className="font-normal">
                          {t("dashboard.usersList.key2")}{" "}
                        </span>
                        <span className="font-semibold">
                          {paymentResults.page * pageSize + 1}
                        </span>
                        <span className="font-normal">
                          {" "}
                          {t("dashboard.usersList.key3")}{" "}
                        </span>
                        <span className="font-semibold">
                          {paymentResults.page * pageSize +
                            paymentResults.results.length}
                        </span>
                        <span className="font-normal">
                          {" "}
                          {t("dashboard.usersList.key4")}{" "}
                        </span>
                        <span className="font-semibold">
                          {paymentResults.total_elements}
                        </span>
                        <span className="font-normal">
                          {" "}
                          {t("dashboard.usersList.key5")}
                        </span>
                      </div>

                      <Pagination
                        pageCount={paymentResults.total_pages}
                        currentPage={paymentResults.page}
                        onPageChange={handlePaymentPageChange}
                      />
                    </div>
                  )}
                </>
              )}

            {!searchLoading && !hasSearched && (
              <Surface>
                <EmptyState
                  icon={<HiSearch size={24} />}
                  title={t("dashboard.advancedSearch.emptyStateTitle")}
                  description={t("dashboard.advancedSearch.emptyStateHint")}
                />
              </Surface>
            )}
          </div>
        </div>
      </div>

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        targetPlan={PlanTier.ENTERPRISE}
        hideFooter={false}
      />
    </div>
  );
};

export default AdvancedSearch;
